import React, {
  createContext, useContext, useMemo, useState, useEffect, useCallback
} from 'react';
import type { ReactNode, Dispatch, SetStateAction } from 'react';
import { getTokens } from './tokens';
import type { Tokens } from './tokens';

// A new key on purpose: the old 'church_erp_theme' was written on every load,
// so it says nothing about whether the user ever chose. Only a toggle writes
// this one, which is what lets the parish default apply to everyone else.
const THEME_KEY = 'church_erp_theme_choice';

/** Whether this browser has picked light or dark itself, via the navbar toggle. */
export const hasStoredTheme = (): boolean => {
  try { return localStorage.getItem(THEME_KEY) !== null; } catch { return false; }
};

/** Forgets the browser's own choice so the parish default applies again. */
export const clearStoredTheme = (): void => {
  try { localStorage.removeItem(THEME_KEY); } catch { /* private browsing */ }
};

export interface ThemeContextValue {
  isDarkMode: boolean;
  toggleTheme: () => void;
  setDarkMode: Dispatch<SetStateAction<boolean>>;
  /** The parish brand colour, or '' when the default purple applies. */
  brandColor: string;
  setBrandColor: Dispatch<SetStateAction<string>>;
  t: Tokens;
}

const ThemeContext = createContext<ThemeContextValue>({
  isDarkMode: false,
  toggleTheme: () => {},
  setDarkMode: () => {},
  brandColor: '',
  setBrandColor: () => {},
  t: getTokens(false)
});

export interface ThemeProviderProps {
  children: ReactNode;
  initialDark?: boolean;
  /** Parish brand colour. Read from the cache on first paint, then the server. */
  initialBrand?: string;
}

export const ThemeProvider = ({
  children, initialDark = false, initialBrand = ''
}: ThemeProviderProps) => {
  const [isDarkMode, setDarkMode] = useState<boolean>(() => {
    const stored = localStorage.getItem(THEME_KEY);
    return stored ? stored === 'dark' : initialDark;
  });
  const [brandColor, setBrandColor] = useState<string>(initialBrand);

  useEffect(() => {
    // The whole app themes through inline JS tokens, so the DOM has no hook for
    // dark mode — which is why native controls (the date/time picker glyph)
    // stayed light-on-dark. This body class gives CSS one, used in index.css to
    // set color-scheme so the browser paints those controls for the theme.
    document.body.classList.toggle('theme-dark', isDarkMode);
  }, [isDarkMode]);

  // Only an explicit toggle is remembered. A browser that has never toggled
  // follows the parish default from Settings (applied by ChurchConfigSync),
  // which it could not do if every mount wrote the current mode back.
  const toggleTheme = useCallback(() => {
    const next = !isDarkMode;
    try { localStorage.setItem(THEME_KEY, next ? 'dark' : 'light'); } catch { /* private browsing */ }
    setDarkMode(next);
  }, [isDarkMode]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      isDarkMode, toggleTheme, setDarkMode, brandColor, setBrandColor,
      // The brand colour replaces the default purple wherever a module has not
      // declared its own accent.
      t: getTokens(isDarkMode, brandColor || undefined)
    }),
    [isDarkMode, toggleTheme, brandColor]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => useContext(ThemeContext);

/**
 * Light, whatever the app is set to. For pages the public sees (the family registration link),
 * which should look the same for every family and never follow a staff member's dark mode.
 *
 * <p>Every UI-kit component reads the theme from context, so overriding it here reaches them all
 * with no prop drilling. The body class does the same for what the browser itself draws (inputs,
 * the date picker, scrollbars) — it wins over `theme-dark` in index.css.
 */
export const AlwaysLight = ({ children }: { children: ReactNode }) => {
  const outer = useContext(ThemeContext);
  useEffect(() => {
    document.body.classList.add('theme-force-light');
    return () => { document.body.classList.remove('theme-force-light'); };
  }, []);
  const value = useMemo<ThemeContextValue>(
    () => ({
      ...outer,
      isDarkMode: false,
      toggleTheme: () => {},
      setDarkMode: () => {},
      t: getTokens(false, outer.brandColor || undefined)
    }),
    [outer]
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// --- Module accent ---
// Each module has its own colour (Attendance orange, Assets teal, ...).
// ModuleRouter wraps the active module in <ModuleAccent>, so every UI-kit
// component inside picks the accent up automatically — no prop drilling.
const AccentContext = createContext<string | undefined>(undefined);

export interface ModuleAccentProps {
  accent?: string;
  children: ReactNode;
}

export const ModuleAccent = ({ accent, children }: ModuleAccentProps) => (
  <AccentContext.Provider value={accent}>{children}</AccentContext.Provider>
);

export const useAccent = (): string | undefined => useContext(AccentContext);

export default ThemeContext;
